import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Promotion } from 'src/app/models/promotion';
import { AutomationsDialogComponent } from '../../automations_dialog/automations_dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { CardType } from 'src/app/models';

@Component({
  selector: 'promotion-card',
  templateUrl: './promotion-card.component.html',
  styleUrls: ['./promotion-card.component.css'],
})
export class PromotionCardComponent {
  countdownInterval: any;
  @Input() promo!: Promotion;
  @Input() reducedSummary: boolean = false;
  @Input() promoState!: string;
  @Output() deletePromotion: EventEmitter<Promotion> = new EventEmitter();

  constructor(public dialog: MatDialog) {}

  ngOnInit(): void {
    if (this.promoState == 'live') {
      this.startPromoCountdownInterval();
    }
  }

  getPromoTitle(promo: Promotion): string {
    if (typeof promo.card === 'object' && promo.card?.title) {
      return promo.card.title;
    }
    return 'Promotion';
  }

  convertDateToTimeZone(date: Date, timeZone: string): Date {
    // Get the time in the target timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };

    const parts = new Intl.DateTimeFormat('en-GB', options)
      .formatToParts(date)
      .reduce((acc: any, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});

    const year = parseInt(parts.year);
    const month = parseInt(parts.month) - 1; // month is 0-indexed
    const day = parseInt(parts.day);
    const hour = parseInt(parts.hour);
    const minute = parseInt(parts.minute);
    const second = parseInt(parts.second);

    // Create a date object representing the local time in the target timezone
    // This is NOT a UTC conversion - it's the actual local time
    return new Date(year, month, day, hour, minute, second);
  }

  getPromoRelativeTime(
    promo: Promotion,
    type: 'start' | 'end',
  ): { label: string; time: string } {
    const timezone = promo.timezone || 'Europe/London';

    const now = new Date();
    const nowInTZ = this.convertDateToTimeZone(now, timezone);

    const targetUTC = new Date(
      type === 'start' ? (promo.start ?? '') : (promo.end ?? ''),
    );
    const targetInTZ = this.convertDateToTimeZone(targetUTC, timezone);

    const ms = targetInTZ.getTime() - nowInTZ.getTime();
    const isPast = ms < 0;
    const absMs = Math.abs(ms);

    if (type === 'end' && !isPast) {
      const totalSeconds = Math.floor(absMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const pad = (n: number) => n.toString().padStart(2, '0');
      const formatted = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      return { label: 'Ends in', time: formatted };
    }

    const totalSeconds = Math.floor(absMs / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts: string[] = [];
    if (days) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    } else {
      if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
      if (minutes) parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }

    const duration = parts.join(', ') || 'just now';

    if (type === 'start') {
      return {
        label: 'Starts in',
        time: isPast ? 'already started' : duration,
      };
    }

    return {
      label: 'Expired',
      time: `${duration} ago`,
    };
  }

  getPromoSummary(promo: Promotion): string {
    const stamps = promo.value ?? 1;
    const timezone = promo.timezone ?? 'Europe/London';

    const from = new Date(promo.start ?? '');
    const to = new Date(promo.end ?? '');

    const friendlyStart = this.formatFriendlyDate(new Date(from ?? ''));
    const friendlyEnd = this.formatFriendlyDate(new Date(to ?? ''));
    const startTime = this.getLocalTimeString(from.toISOString(), timezone);
    const endTime = this.getLocalTimeString(to.toISOString(), timezone);
    const isPoints =
      promo?.type === CardType.point || promo?.type === CardType.openEndedPoint;

    if (this.reducedSummary) {
      return `📣 ${stamps} ${isPoints ? 'point' : 'stamp'}${
        stamps > 1 ? 's' : ''
      }`;
    } else {
      return `📣 ${stamps} ${isPoints ? 'point' : 'stamp'}${
        stamps > 1 ? 's' : ''
      } per scan between ${friendlyStart} ${startTime} and ${friendlyEnd} ${endTime} ${this.getPromoDurationString(
        from,
        to,
      )}`;
    }
  }

  getLocalTimeString(dateString: string, timeZone: string): string {
    const date = new Date(dateString);

    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone,
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const hour = parts.find((part) => part.type === 'hour')?.value;
    const minute = parts.find((part) => part.type === 'minute')?.value;

    return `${hour}:${minute}`;
  }

  formatFriendlyDate(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    const getOrdinal = (n: number) => {
      if (n > 3 && n < 21) return 'th';
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  }

  startPromoCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      const now = new Date();
      const end = new Date(this.promo.end ?? '');
      const timezone = this.promo.timezone ?? 'Europe/London';

      const endInTZ = this.convertDateToTimeZone(end, timezone);
      const nowInTZ = this.convertDateToTimeZone(now, timezone);

      const diffMs = endInTZ.getTime() - nowInTZ.getTime();
      if (diffMs > 0) {
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (n: number) => n.toString().padStart(2, '0');
        this.countdownInterval = `${pad(hours)}:${pad(minutes)}:${pad(
          seconds,
        )}`;
      } else {
        this.countdownInterval = '00:00:00';
      }
    }, 1000);
  }

  getPromoDurationString(start: Date, end: Date): string {
    const ms = end.getTime() - start.getTime();
    if (ms <= 0) return '(Invalid time range)';

    const totalHours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;

    const parts = [];
    if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);

    return parts.length ? `(${parts.join(', ')})` : '(Less than 1 hour)';
  }

  get leading() {
    if (this.promoState == 'live') {
      return this.getPromoRelativeTime(this.promo, 'end');
    } else if (this.promoState == 'upcoming') {
      return this.getPromoRelativeTime(this.promo, 'start');
    } else {
      return null;
    }
  }

  get buttonTitle() {
    if (this.reducedSummary) {
      return null;
    }
    return this.promoState == 'live'
      ? 'Stop'
      : this.promoState == 'upcoming'
        ? 'Cancel'
        : null;
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  openAutomations(): void {
    if (!this.reducedSummary) return;

    this.dialog.open(AutomationsDialogComponent, {
      data: {
        selectedIndex: 0,
        hideBirthdayClubTab: true,
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '800px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '80vh',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }
}
